/**
 * A small library of reusable workouts the athlete can drop onto any calendar
 * day from the Training view. Stored in localStorage; ships with a few sensible
 * defaults (incl. 55-min spinning). Each exercise becomes an intervals.icu event
 * whose description carries the duration so the TSS / intensity charts pick it up.
 */
import { SportType } from "../types/training";

export interface SavedExercise {
  id: string;
  name: string;
  sport: SportType;
  durationMin: number;
  description: string; // intervals.icu-style text; must contain the duration
}

export interface NewEventPayload {
  start_date_local: string;
  name: string;
  type: string;
  description: string;
  category: string;
}

const KEY = "saved-exercises";

export const DEFAULT_EXERCISES: SavedExercise[] = [
  { id: "spinning-55", name: "Spinning", sport: "Ride", durationMin: 55, description: "55min Z2 HR spinning" },
  { id: "easy-run-45", name: "Lugn löpning", sport: "Run", durationMin: 45, description: "45min Z2 HR lugnt" },
  { id: "long-run-90", name: "Långpass", sport: "Run", durationMin: 90, description: "1h30m Z2 HR långpass" },
  { id: "gym-60", name: "Gym", sport: "WeightTraining", durationMin: 60, description: "1h styrketräning" },
];

export function loadSavedExercises(): SavedExercise[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [...DEFAULT_EXERCISES];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...DEFAULT_EXERCISES];
  } catch {
    return [...DEFAULT_EXERCISES];
  }
}

export function saveSavedExercises(list: SavedExercise[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

/** Add a new exercise (id generated) and persist. Returns the updated list. */
export function addSavedExercise(ex: Omit<SavedExercise, "id">): SavedExercise[] {
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  const next = [...loadSavedExercises(), { ...ex, id }];
  saveSavedExercises(next);
  return next;
}

export function removeSavedExercise(id: string): SavedExercise[] {
  const next = loadSavedExercises().filter(e => e.id !== id);
  saveSavedExercises(next);
  return next;
}

/** Build the intervals.icu event payload for dropping this exercise on a date. */
export function exerciseToEventPayload(ex: SavedExercise, dateStr: string): NewEventPayload {
  return {
    start_date_local: `${dateStr}T10:00:00`,
    name: ex.name,
    type: ex.sport,
    description: ex.description,
    category: "WORKOUT",
  };
}
